import { GRID_CHECKBOX_SELECTION_COL_DEF, GridColDef, GridEventListener } from "@mui/x-data-grid";
import { GridCellSelectionModel } from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { motion } from "motion/react";
import { MutableRefObject, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import CommonGridTable from "@/components/CommonGridTable";
import EstimatedPeopleCountColumn from "@/components/table/EstimatedCompanionsColumn";
import { PassengerColumn } from "@/components/table/PassengerColumn";
import orderColumn from "@/components/table/orderColumn";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import HotelNameColumn from "@/pages/HotelPlanning/arrival/components/table/components/HotelNameColumn";
import useDriversContext from "@/providers/data/DriversContext";
import { IHotelPlanningDepartureResponseItem, getHotelDepartureRowId } from "@/schemas/hotelPlanningDepartureSchema";
import {
    useHotelPlanningDepartureActions,
    useHotelPlanningDepartureIsFullScreen,
    useHotelPlanningDepartureIsPastDate,
    useHotelPlanningDepartureRowModesModel,
} from "@/stores/use-hotel-planning-departure-table";
import { onGridCellKeyDownCallback } from "@/utils/grid";

import TrackingActionsColumn from "./HotelDepartureActionsColumn";
import { HotelDepartureAssignationColumn } from "./HotelDepartureAssignationColumn";
import HotelDepartureDateRangeColumn from "./HotelDepartureDateRangeColumn";
import HotelDeparturePassengerTypeColumn from "./HotelDeparturePassengerTypeColumn";
import HotelDepartureStatusColumn from "./HotelDepartureStatusColumn";
import HotelDepartureStatusFilter from "./HotelDepartureStatusFilter";
import HotelDepartureToolbar from "./HotelDepartureToolbar";

const ASSIGNMENT_FIELDS: ("assignedUser" | "labelledUser")[] = ["assignedUser", "labelledUser"];

function normalizeAssignmentFields(
    row: IHotelPlanningDepartureResponseItem,
    drivers: { userId: string; name: string }[]
): IHotelPlanningDepartureResponseItem {
    let changed = false;
    const next = { ...row };
    for (const field of ASSIGNMENT_FIELDS) {
        const val = next[field];
        if (typeof val !== "string") continue;
        const driver = drivers.find((d) => d.userId === val || d.name === val);
        next[field] =
            driver != null
                ? {
                      userId: driver.userId,
                      userName: driver.name,
                  }
                : null;
        changed = true;
    }
    return changed ? next : row;
}

function HotelPlanningDepartureTable({
    rows,
    loading,
    apiRef,
    allRows,
    refetch,
}: {
    rows: IHotelPlanningDepartureResponseItem[];
    loading: boolean;
    apiRef: MutableRefObject<GridApiPremium>;
    refetch: () => void;
    allRows: IHotelPlanningDepartureResponseItem[];
}) {
    const { size } = useWindowDimensions();
    const { drivers } = useDriversContext();
    const isFullScreen = useHotelPlanningDepartureIsFullScreen();
    // History (past day) is read-only: no cell edits, no row selection, no progress actions.
    const isPastDate = useHotelPlanningDepartureIsPastDate();
    const { t } = useTranslation();
    const {
        processRowUpdate: storeProcessRowUpdate,
        handleRowModesModelCancel,
        updateSelectedRows,
    } = useHotelPlanningDepartureActions();
    const rowModesModel = useHotelPlanningDepartureRowModesModel();
    const selectionModel = useRef<GridCellSelectionModel>();

    const processRowUpdate = useCallback(
        (newRow: IHotelPlanningDepartureResponseItem, oldRow: IHotelPlanningDepartureResponseItem) => {
            const normalized = normalizeAssignmentFields(newRow, drivers);
            return storeProcessRowUpdate(normalized, oldRow);
        },
        [drivers, storeProcessRowUpdate]
    );

    const setCellSelectionModel = useCallback(
        (selection: GridCellSelectionModel) => {
            selectionModel.current = selection;
        },
        [selectionModel]
    );

    const onCellKeyDown = useCallback<GridEventListener<"cellKeyDown">>(
        ({ cellMode, hasFocus, field, isEditable }, event, { api }) => {
            onGridCellKeyDownCallback(
                { cellMode, hasFocus, field, isEditable },
                event,
                { api },
                selectionModel.current || {},
                processRowUpdate,
                handleRowModesModelCancel
            );
        },
        [selectionModel, processRowUpdate, handleRowModesModelCancel]
    );

    const columns: GridColDef[] = useMemo<GridColDef[]>(
        () => [
            ...(isPastDate
                ? []
                : [
                      {
                          ...GRID_CHECKBOX_SELECTION_COL_DEF,
                          headerClassName: "checkbox-header",
                      },
                  ]),
            orderColumn(),
            PassengerColumn({ size, t, raw: true, showDetails: false }),
            HotelDeparturePassengerTypeColumn({ t }),
            EstimatedPeopleCountColumn<IHotelPlanningDepartureResponseItem>({
                t,
                getIsFlightArrival: () => true,
                getValueGetter: (row) => (row.companionsCount || 0) + 1,
                getCompanionsLength: (row) => row.companionsCount || 0,
            }),
            HotelDepartureStatusColumn({ t }),
            HotelDepartureDateRangeColumn({ t, isPastDate }),
            HotelNameColumn({ t }),
            HotelDepartureAssignationColumn({ t, drivers, fieldName: "assignedUser" }),
            HotelDepartureAssignationColumn({ t, drivers, fieldName: "labelledUser" }),
            ...(isPastDate ? [] : [TrackingActionsColumn({ allRows })]),
        ],
        [t, size, drivers, allRows, isPastDate]
    );

    return (
        <>
            <div className="flex w-full grow min-h-64   overflow-auto  h-full  ">
                <motion.div
                    initial={{ height: "100%", position: "static", width: "100%" }}
                    animate={{
                        height: isFullScreen ? "100vh" : "100%",
                        width: isFullScreen ? "100vw" : "100%",
                        position: isFullScreen ? "fixed" : "static",
                        padding: isFullScreen ? (size.md ? 16 : 4) : 0,
                        top: 0,
                        left: 0,
                        background: isFullScreen ? "white" : "transparent",
                        zIndex: isFullScreen ? 1000 : "auto",
                    }}
                    transition={{ duration: 0.3 }}
                >
                    <CommonGridTable<IHotelPlanningDepartureResponseItem>
                        toolbar={() => <HotelDepartureToolbar allRows={allRows} reloadTable={refetch} />}
                        loading={loading}
                        rows={rows}
                        columns={columns}
                        api={apiRef}
                        allRows={allRows}
                        rowModesModel={rowModesModel}
                        getRowId={getHotelDepartureRowId}
                        isRowSelectable={(params) =>
                            !isPastDate &&
                            !!params.row.assignedUser &&
                            (params.row.type === "clinic" || !!params.row.taskId)
                        }
                        isCellEditable={(params) => !isPastDate && (params.colDef.editable ?? false)}
                        processRowUpdate={processRowUpdate}
                        initialState={{
                            pinnedColumns: {
                                left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, "index", "name"],
                                right: ["actions"],
                            },
                        }}
                        checkboxSelection={!isPastDate}
                        onCellSelectionModelChange={setCellSelectionModel}
                        onCellKeyDown={onCellKeyDown}
                        onRowSelectionModelChange={updateSelectedRows}
                        startElement={HotelDepartureStatusFilter}
                        disableRowSelectionOnClick={true}
                    />
                </motion.div>
            </div>
        </>
    );
}

export default HotelPlanningDepartureTable;
